import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import SearchIcon from '@mui/icons-material/Search'
import Modal from '@mui/material/Modal'
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported'
import Title from '../../component/Title'
import ResetFilterButton from '../../component/ResetFilterButton'
import { LIST_FILTER_SEARCH_FIELD_SX, LIST_FILTER_SEARCH_BUTTON_SX, LIST_FILTER_DIVIDER_SX, LIST_FILTER_SELECT_FIELD_SX, ListFilterBar, ListFilterForm } from '../../component/ListFilterLayout'
import API from '../../component/Axios'
import Pagination from '../../component/Pagination'
import EditIcon from '../../component/EditIcon'
import DeleteAlertDialog from '../../component/DeleteAlertDialog'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import ListTableContainer from '../../component/ListTableContainer'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import BottomButtonGroup, { FORM_COMPACT_ACTION_BUTTON_SX } from '../../component/BottomButtonGroup'
import StatusChip from '../../component/StatusChip'
import { buildListSearch as buildListSearchQuery, createInitialListState, parseNumberParam } from '../../utils/listPageState'
import { renderDateTime } from '../../utils/dateDisplay'

const DEFAULT_SEARCH_SELECT = {
    searchStatus: '',
    searchDeviceType: '',
}

const SEARCH_SELECT_PARAMS = {
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
    searchDeviceType: {
        param: 'device',
        defaultValue: DEFAULT_SEARCH_SELECT.searchDeviceType,
    },
}

const style2 = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: 'calc(100vw - 32px)', sm: 600 },
    maxWidth: '100%',
    bgcolor: 'background.paper',
    border: '1px solid rgba(148, 163, 184, 0.32)',
    borderRadius: '8px',
    boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
    outline: 'none',
    p: { xs: 2, sm: 3 },
}

const REVIEW_EVENT_STATUS_CHIP = {
    1: { label: '미확인', color: 'warning' },
    2: { label: '지급완료', color: 'success' },
}

const reviewEventImagePlaceholderSx = {
    width: 46,
    height: 46,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: 'action.hover',
    color: 'text.disabled',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
}

function ReviewEventImageCell({ row }) {
    if (row.image) {
        return (
            <img
                className="listImg"
                src={`${process.env.REACT_APP_AWS_S3_URL}/${row.image}`}
                alt={`${row.user?.nickName || '리뷰 이벤트'} 이미지`}
            />
        )
    }

    return (
        <Box sx={reviewEventImagePlaceholderSx} aria-label="리뷰 이벤트 이미지 없음">
            <ImageNotSupportedIcon sx={{ fontSize: 18 }} />
        </Box>
    )
}

export default function AppReviewEvents() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(createInitialListState({
        location,
        defaultSearchSelect: DEFAULT_SEARCH_SELECT,
        selectParams: SEARCH_SELECT_PARAMS,
        getExtraState: ({ searchParams }) => ({
            reviewEventDialogId: searchParams.get('dialog') === 'point'
                ? parseNumberParam(searchParams.get('reviewEventId'), null)
                : null,
        }),
    }))
    const initialListState = initialListStateRef.current
    const modalHydratedRef = useRef(false)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')
    const deleteInFlightRef = useRef(false)
    const silentListRefreshRef = useRef(false)
    const isBusy = saving || actionLoading

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appReviewEvent/getList', {
                page: curPage, offset: offset, searchText: searchQuery, searchSelect: searchSelect
            })
            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '리뷰 이벤트 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '리뷰 이벤트 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [curPage, history, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        if (isBusy) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        setCurPage(1)
        setSearchQuery((searchText || '').trim())
        setSearchSelect({
            ...searchSelect,
            [e.target.name]: e.target.value
        })
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const resetFilters = () => {
        if (isBusy) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    // 입력값과 실제 조회값을 분리해 Enter/검색 버튼 시점만 제어한다.
    const searchButtonClick = () => {
        if (isBusy) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
            return
        }

        if (searchQuery !== trimmedSearchText) {
            return
        }

        getData(false)
    }

    const handleDelete = async (id) => {
        if (deleteInFlightRef.current || saving) {
            return
        }

        deleteInFlightRef.current = true
        setActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/appReviewEvent/delete', { reviewEventId: id })

            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('리뷰 이벤트를 삭제했습니다.')

                if (params.length === 1 && curPage > 1) {
                    setCurPage(curPage - 1)
                } else {
                    await getData()
                }
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('리뷰 이벤트를 삭제하지 못했습니다.')
        } finally {
            setActionLoading(false)
            deleteInFlightRef.current = false
        }
    }

    const [openModal, setOpenModal] = useState(Boolean(initialListState.reviewEventDialogId))
    const [id, setId] = useState(initialListState.reviewEventDialogId)
    const selectedReviewEvent = params.find((row) => Number(row.reviewEventId) === Number(id))

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage,
            searchQuery,
            searchSelect,
            selectParams: SEARCH_SELECT_PARAMS,
            extraParams: [
                {
                    param: 'dialog',
                    value: 'point',
                    shouldInclude: () => openModal && Boolean(id),
                },
                {
                    param: 'reviewEventId',
                    value: id,
                    shouldInclude: (value) => openModal && Boolean(value),
                },
            ],
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, id, location.pathname, location.search, openModal, searchQuery, searchSelect])

    const handleUpdateClick = (id) => {
        if (isBusy) {
            return
        }

        setId(id)
        setOpenModal(true)
    }

    const updateCancel = () => {
        if (saving) {
            return
        }

        setId(null)
        setOpenModal(false)
    }

    const updateData = async () => {
        if (isBusy) {
            return
        }

        if (!id) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('지급할 리뷰 이벤트를 선택하세요.')
            return
        }

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appReviewEvent/update', { reviewEventId: id })
            if (data.code === 200) {
                setId(null)
                setOpenModal(false)
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar('리뷰 이벤트 포인트 지급이 완료되었습니다.')
                await getData()
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('리뷰 이벤트를 승인하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || String(searchSelect.searchStatus) !== String(DEFAULT_SEARCH_SELECT.searchStatus)
        || String(searchSelect.searchDeviceType) !== String(DEFAULT_SEARCH_SELECT.searchDeviceType)

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="리뷰 이벤트 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={isBusy}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 리뷰 이벤트가 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={isBusy} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 리뷰 이벤트가 없습니다."
                description="리뷰 이벤트가 등록되면 이 목록에 표시됩니다."
            />
        )
    }

    // 새로고침으로 복귀했을 때 현재 목록 데이터에서 다시 찾아 같은 포인트 지급 모달을 연다.
    useEffect(() => {
        if (loading || modalHydratedRef.current || !initialListState.reviewEventDialogId) {
            return
        }

        const targetRow = params.find((row) => Number(row.reviewEventId) === Number(initialListState.reviewEventDialogId))
        modalHydratedRef.current = true

        if (!targetRow) {
            setId(null)
            setOpenModal(false)
            return
        }

        setId(targetRow.reviewEventId)
        setOpenModal(true)
    }, [initialListState.reviewEventDialogId, loading, params])

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {/* 타이틀 */}
                    <Title>리뷰이벤트 리스트</Title>
                    {/* 검색폼 */}
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={isBusy} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="닉네임"
                                type="search" 
                                name="searchText"
                                value={searchText}
                                disabled={isBusy}
                                onChange={(e) => setSearchText(e.target.value)}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={isBusy}><SearchIcon /></IconButton>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            <TextField
                                label="기기"
                                select
                                name="searchDeviceType"
                                value={searchSelect.searchDeviceType}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value={'and'}>Android</MenuItem>
                                <MenuItem value={'ios'}>iOS</MenuItem>
                            </TextField>
                            <TextField
                                label="지급상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                size="small"
                                disabled={isBusy}
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value=''>전체</MenuItem>
                                <MenuItem value={1}>미확인</MenuItem>
                                <MenuItem value={2}>지급완료</MenuItem>
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>
                    
                    {/* 테이블 */}
                    <Box sx={{ opacity: isBusy ? 0.5 : 1, pointerEvents: isBusy ? 'none' : 'auto' }}>
                        <ListTableContainer>
                        <Table size="small" sx={{ minWidth: 900 }}>
                            <caption>Total Count: {totalCount}</caption>
                            <TableHead>
                                <TableRow>
                                    <TableCell>No</TableCell>
                                    <TableCell>이미지</TableCell>
                                    <TableCell>닉네임</TableCell>
                                    <TableCell>기기</TableCell>
                                    <TableCell>지급상태</TableCell>
                                    <TableCell>등록일</TableCell>
                                    <TableCell></TableCell>
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow key={row.reviewEventId} hover>
                                            <TableCell>{row.rownum}</TableCell>
                                            <TableCell>
                                                <ReviewEventImageCell row={row} />
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ fontWeight: 700 }}>
                                                    {row.user?.nickName || '-'}
                                                </Box>
                                            </TableCell>
                                            <TableCell>{row.deviceType || '-'}</TableCell>
                                            <TableCell>
                                                <StatusChip status={row.status} config={REVIEW_EVENT_STATUS_CHIP} fallbackLabel="상태 없음" />
                                            </TableCell>
                                            <TableCell>{renderDateTime(row.regTime)}</TableCell>
                                            <TableCell align="right">
                                                {row.status === 1 &&
                                                    <Button
                                                        variant="contained"
                                                        color="success"
                                                        size="small"
                                                        sx={{ ...FORM_COMPACT_ACTION_BUTTON_SX, minWidth: 86 }}
                                                        onClick={() => handleUpdateClick(row.reviewEventId)}
                                                        disabled={isBusy}
                                                    >
                                                        포인트지급
                                                    </Button>
                                                }
                                            </TableCell>
                                            <TableCell align="right">
                                                <ListActionGroup>
                                                    <EditIcon path={'appReviewEvent'} id={row.reviewEventId} search={location.search} curPage={curPage} searchText={searchQuery} searchSelect={searchSelect} disabled={isBusy}/>
                                                    <DeleteAlertDialog handleDelete={handleDelete} id={row.reviewEventId} disabled={isBusy}/>
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        </ListTableContainer>
                    </Box>
                    <Modal open={openModal} onClose={updateCancel} aria-label="리뷰 이벤트 포인트 지급 확인">
                        <Box
                            component="form"
                            sx={style2}
                            onSubmit={(event) => {
                                event.preventDefault()
                                updateData()
                            }}
                        >
                            <Title>리뷰 이벤트 포인트를 지급하시겠습니까?</Title>
                            <Grid container spacing={2} sx={{ marginTop: 2 }}>
                                <Grid item xs={12}>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 1.5,
                                            borderRadius: '4px',
                                            borderColor: 'rgba(148, 163, 184, 0.24)',
                                            bgcolor: '#f8fafc',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minHeight: 240,
                                                maxHeight: 360,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'auto',
                                                borderRadius: '4px',
                                                bgcolor: '#fff',
                                                border: '1px solid rgba(148, 163, 184, 0.24)',
                                                mb: 1,
                                            }}
                                        >
                                            {selectedReviewEvent?.image ? (
                                                <Box
                                                    component="img"
                                                    src={`${process.env.REACT_APP_AWS_S3_URL}/${selectedReviewEvent.image}`}
                                                    alt="리뷰 이벤트 이미지"
                                                    sx={{
                                                        width: '100%',
                                                        maxHeight: 360,
                                                        objectFit: 'contain',
                                                        display: 'block',
                                                    }}
                                                />
                                            ) : (
                                                <Box sx={{ color: 'text.secondary', fontSize: 14 }}>
                                                    등록된 이미지가 없습니다.
                                                </Box>
                                            )}
                                        </Box>
                                        <Box sx={{ color: 'text.secondary', fontSize: 13 }}>
                                            {selectedReviewEvent?.user?.nickName ? `닉네임: ${selectedReviewEvent.user.nickName}` : '선택한 리뷰 이벤트 이미지입니다.'}
                                            {selectedReviewEvent?.deviceType ? ` · 기기: ${selectedReviewEvent.deviceType}` : ''}
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={12} md={12}>
                                    <BottomButtonGroup
                                        variant="formAction"
                                        visible
                                        backLabel="취소"
                                        submitDisabled={saving}
                                        submitLabel={saving ? '지급 중...' : '포인트지급'}
                                        onBackClick={updateCancel}
                                        actionTitle="포인트 지급"
                                        actionDescription="선택한 리뷰 이벤트에 포인트를 지급합니다."
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Modal>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
